import React, { useEffect, useMemo, useRef, useState } from "react";
import { Stripe, StripeError } from "@stripe/stripe-js";

import {useActionApi, usePersistedState} from "hooks";
import { entities } from "reduxStore";
import Api, { config } from "api";
import { navigateToCordovaLocationsScreen } from 'utils'
import { getStripe } from "./utils";
import { Token, SavedCard, StripeCard } from "./types";
import {
    COUNTRY_CA,
    PAYMENT_PURPOSE_REVENUE,
    getRevenueCustomerId,
    getRevenueStripeCountry,
    hasOfficeRevenuePaymentInfo,
    toRevenueBillingLocation,
} from "utils/paymentPurpose";

import Popup from "components/Popup";
import { Location } from "components/BillingLocationSelector";
import { SimpleModal } from "components/Modal";
import ModalTitleContainer from "components/Modal/components/ModalTitleContainer";

import { Tokens } from './popups/Tokens'
import { Checkout } from './popups/Checkout'
import { Confirmation } from './popups/Confirmation'
import { Failure } from './popups/Failure'
import {UserAccountProps} from "utils/types";
import { LOCATION_IS_CENTRAL_BOOKED } from "utils/constants";
import { Analytics } from "utils/analytics";

// @ts-ignore
const app = window.app as any;
// @ts-ignore
const TempStars = window.TempStars;

interface PurchaseTokensFlowProps {
    onClose: () => void,
    onSuccess: () => void
}

enum PopupType {
    tokens = 'tokens',
    checkout = 'checkout',
    confirmation = 'confirmation',
    failure = 'failure'
}

const PurchaseTokensFlow = ({ onClose, onSuccess }: PurchaseTokensFlowProps) => {
    const [stripe, setStripe] = useState<Stripe | null>(null);

    useEffect(() => {
        getStripe(COUNTRY_CA).then(stripe => setStripe(stripe))
    }, []);

    const [currentPopup, setCurrentPopup] = useState<PopupType>(PopupType.tokens);
    const [selectedToken, setSelectedToken] = useState<Token | null>(null);
    const [selectedBillingLocation, setSelectedBillingLocation] = useState<Location | null>(null);

    const [billingCardError, setBillingCardError] = useState('');
    const [savedBillingCard, setSavedBillingCard] = useState<SavedCard | null>(null);
    const [updatedBillingStripeCard, setUpdatedBillingStripeCard] = useState<StripeCard | null>(null);
    const [isBillingCardLoading, setIsBillingCardLoading] = useState<boolean>(false);

    const dentistUser = usePersistedState({
        entityName: entities.user
    })[0] as UserAccountProps

    const countryCode = usePersistedState({
        entityName: entities.countryCode,
    })[0]

    const billingLocations = usePersistedState({
        entityName: entities.dentistBillingLocations,
        initialValue: [],
    })[0] as Location[]

    const revenueBillingLocations = useMemo(
        () => billingLocations.map(toRevenueBillingLocation),
        [billingLocations],
    );
    const is123Dentist = Number(selectedBillingLocation?.isCentralBooked) === LOCATION_IS_CENTRAL_BOOKED._123_DENTIST;

    const [purchaseTokensLoading, purchaseTokensError, purchaseTokens] = useActionApi({
        apiMethod: Api.purchaseTokens,
        onSuccess: () => {
            setBillingCardError('');
            app.hidePreloader();
            setCurrentPopup(PopupType.confirmation);
        },
        onError: () => {
            setBillingCardError('');
            app.hidePreloader();
            setCurrentPopup(PopupType.failure);
        },
    })

    const onBillingLocationSelect = async (location: Location) => {
        setSelectedBillingLocation(location);

        const revenueCustomerId = hasOfficeRevenuePaymentInfo(location)
            ? getRevenueCustomerId(location)
            : null;
        const revenueStripeCountry = getRevenueStripeCountry(location);
        const savedLocationCard = revenueCustomerId && revenueStripeCountry !== undefined
            ? await Api.cardById(revenueCustomerId, revenueStripeCountry)
            : null;
        setSavedBillingCard(savedLocationCard || null)
        setUpdatedBillingStripeCard(null)
    }

    useEffect(() => {
        if (!selectedBillingLocation && revenueBillingLocations.length) {
            const firstNon123D = revenueBillingLocations.find(
                (loc) => loc.isCentralBooked !== LOCATION_IS_CENTRAL_BOOKED._123_DENTIST
            );
            onBillingLocationSelect(firstNon123D || revenueBillingLocations[0])
        }
    }, [revenueBillingLocations, selectedBillingLocation])

    const country = dentistUser.dentist?.countryStr || countryCode as string;

    const hasTracked123DRestriction = useRef(false);
    useEffect(() => {
        if (is123Dentist && selectedBillingLocation && !hasTracked123DRestriction.current) {
            hasTracked123DRestriction.current = true;
            Analytics.track("Job Board Token Purchase Restricted - 123D", {
                locationId: selectedBillingLocation.id,
                practiceName: selectedBillingLocation.practiceName,
            });
        }
    }, [is123Dentist, selectedBillingLocation]);

    if (is123Dentist) {
        return (
            <SimpleModal isOpen={true} onClose={onClose}>
                <ModalTitleContainer title="Corporate Restriction" onClose={onClose} />
                <div style={{ fontSize: '1rem', textAlign: 'center', marginBottom: '2rem' }}>
                    Corporate has restricted the use of the Job Board access. If you are interested in using the Job Board, please reach out to Corporate.
                </div>
                <button
                    onClick={onClose}
                    style={{
                        display: 'block',
                        margin: '0 auto',
                        minWidth: '20rem',
                        maxWidth: '24rem',
                        padding: '0.75rem 1.5rem',
                        backgroundColor: 'var(--primary-blue, #0B4598)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontSize: '1rem',
                        fontWeight: 600,
                        fontFamily: "'Poppins', sans-serif",
                        cursor: 'pointer',
                    }}
                >
                    OK
                </button>
            </SimpleModal>
        );
    }

    const saveUpdateBillingStripeCard = async () => {
        const updateErrorMessage = (error: StripeError) => {
            if (error.message && (error.type === "card_error" || error.type === "validation_error")) {
                setBillingCardError(error.message);
            } else {
                setBillingCardError("An unexpected error occurred.");
            }
        }

        try {
            if (!stripe || !selectedBillingLocation || !updatedBillingStripeCard) {
                return;
            }
            setIsBillingCardLoading(true);

            const intent = await Api.createSetupIntent(selectedBillingLocation.id, {
                paymentPurpose: PAYMENT_PURPOSE_REVENUE,
            });
            const { error } = await stripe.confirmCardSetup(intent?.client_secret, {
                payment_method: {
                    card: updatedBillingStripeCard
                }
            });


            if (error) {
                setIsBillingCardLoading(false);
                updateErrorMessage(error as any);
                return false
            }

            await Api.updatePaymentInfo(selectedBillingLocation.id, {
                paymentPurpose: PAYMENT_PURPOSE_REVENUE,
            });
            await TempStars.User.refresh();
            setIsBillingCardLoading(false);

            return true;
        } catch (error: any) {
            updateErrorMessage(error as any);
            setIsBillingCardLoading(false);
            return false;
        }
    }

    const onCheckoutSubmit = async () => {
        app.showPreloader('Purchasing Tokens');

        const creditCardIsValid = !savedBillingCard && updatedBillingStripeCard
            ? await saveUpdateBillingStripeCard()
            : true;

        if (!creditCardIsValid || !selectedBillingLocation || !selectedToken) {
            app.hidePreloader();
            return;
        }

        purchaseTokens({
            locationId: selectedBillingLocation.id,
            tokensAmount: selectedToken.count,
        })
    }

    return <>
        <Popup opened={currentPopup === PopupType.tokens}>
            <Tokens
                onCloseButtonClick={onClose}
                onTokenClick={(token: Token) => {
                    setSelectedToken(token)
                    setCurrentPopup(PopupType.checkout)
                }}
                country={country}
                infoLink={
                    country === 'US'
                        ? config.US.tokensLink
                        : config.CA.tokensLink
                    }
            />
        </Popup>
        <Popup opened={currentPopup === PopupType.checkout}>
            {selectedToken ? (
                <Checkout
                    stripe={stripe}
                    onSubmit={onCheckoutSubmit}
                    savedBillingCard={savedBillingCard}
                    setSavedBillingCard={setSavedBillingCard}
                    updatedBillingStripeCard={updatedBillingStripeCard}
                    setUpdatedBillingStripeCard={setUpdatedBillingStripeCard}
                    locations={revenueBillingLocations}
                    onSelectedLocationChange={onBillingLocationSelect}
                    selectedLocation={selectedBillingLocation}
                    country={country}
                    onCloseButtonClick={onClose}
                    selectedToken={selectedToken}
                    onTokenSelect={setSelectedToken}
                    disabled={purchaseTokensLoading || isBillingCardLoading}
                    errorMessage={billingCardError}
                    onAddLocationClick={() => {
                        // T2-6015: Close this popup before routing to the React
                        // Add Location page so the fixed overlay does not stay
                        // above the next screen.
                        onClose();
                        navigateToCordovaLocationsScreen();
                    }}
                />
            ) : null}
        </Popup>
        <Popup opened={currentPopup === PopupType.confirmation}>
            {selectedToken ? (
                <Confirmation
                    onConfirm={onSuccess}
                    selectedToken={selectedToken}
                />
            ) : null}
        </Popup>
        <Popup opened={currentPopup === PopupType.failure}>
            <Failure
                onClose={onClose}
                onConfirm={() => {
                    setSavedBillingCard(null);
                    setBillingCardError('Please update payment info.')
                    setCurrentPopup(PopupType.checkout);
                }}
            />
        </Popup>
    </>;
}

export default PurchaseTokensFlow;
